import { isEntityType, type EntityType } from '../../domain/labels.js';
import { countWord } from '../../domain/pluralise.js';
import { rowLabel } from '../../domain/rowLabel.js';
import type { SentencePart } from '../../domain/sentence.js';
import type { PushOpRow } from './PushOps.js';
import type { PushCells } from './pushSummary.js';

/**
 * What the review says before anything is drawn from a record: the title (naming the ticked
 * environments), and which ticked environment the record pane is drawn against. Kept apart from
 * the modal that draws it, the same way `pushSummary.ts` keeps the dry-run sentences apart from
 * it.
 */

/** A run of the title: words, or an environment's name (`mono`) — drawn monospace, never broken. */
const word = (text: string): SentencePart => ({ before: text });
const env = (name: string): SentencePart => ({ before: '', mono: name });

/** `production-ap`, `dev and staging`, `3 environments`. */
const environmentsPhrase = (names: readonly string[]): SentencePart[] =>
    names.length > 2
        ? [word(`${names.length} environments`)]
        : names.flatMap((name, index) => [...(index > 0 ? [word(' and ')] : []), env(name)]);

/**
 * `Push all drift & missing to production-ap`, `Push 3 records to dev and staging`, or just
 * `Push 12 records` before anything is ticked — a push to nowhere names no direction.
 */
export const reviewTitleParts = (cells: PushCells, tickedNames: readonly string[]): SentencePart[] => {
    const subject = cells === null ? 'all drift & missing' : countWord(cells.length, 'record');
    return tickedNames.length === 0
        ? [word(`Push ${subject}`)]
        : [word(`Push ${subject} to `), ...environmentsPhrase(tickedNames)];
};

/**
 * Which ticked environment the record pane is drawn against: the one on screen while it stays
 * ticked, else the first ticked in the checklist's own order, else none.
 */
export const viewedEnvironment = (
    names: readonly string[],
    ticked: ReadonlySet<string>,
    current: string | null,
): string | null =>
    current !== null && ticked.has(current) ? current : (names.find((name) => ticked.has(name)) ?? null);

/** What one op's field **Adopt ▾** adopts from — `useFieldAdoption().rowActionFor`'s argument. */
export type AdoptTarget = { type: EntityType; key: string; environment: string; recordLabel: string };

/**
 * An update the push would make, not yet run, whose "before" is what the environment holds: either
 * adopt takes that value instead of overwriting it. A create has nothing in the environment to
 * adopt; an op already run is history.
 */
export const adoptTargetOf = (op: PushOpRow, environment: string | null): AdoptTarget | null => {
    if (!environment || op.op !== 'update' || !op.before || op.result || !isEntityType(op.entityType)) return null;
    return { type: op.entityType, key: op.entityKey, environment, recordLabel: rowLabel(op.title, op.entityKey).label };
};
